import { useEffect, useMemo, useRef, useState } from 'react';
import { computeCoverage, type CoverageResult } from './coverage-geometry';
import type { ScenarioFootprint } from './footprints';

/** A request to the coverage worker. */
export interface CoverageRequest {
  id: number;
  footprints: readonly ScenarioFootprint[];
  blocked: boolean;
  loading: boolean;
}

/** The coverage worker's answer, with the id of the request it answers. */
export interface CoverageResponse {
  id: number;
  result: CoverageResult;
}

/** The part of a Worker the hook uses, so a test can stand one in. */
export interface CoverageWorker {
  postMessage: (request: CoverageRequest) => void;
  onmessage: ((event: MessageEvent<CoverageResponse>) => void) | null;
  /** The worker failed to load or threw: the hook falls back to the main thread. */
  onerror: ((event: Event) => void) | null;
  /** A message couldn't be deserialised: the same fallback. */
  onmessageerror: ((event: MessageEvent) => void) | null;
  terminate: () => void;
}

/**
 * A coverage worker, or null where there is no Worker (jsdom, an old browser): the hook then works
 * on the main thread. Vite bundles the worker from the `new URL(..., import.meta.url)` form.
 *
 * @return {CoverageWorker | null} The worker.
 */
export function createCoverageWorker(): CoverageWorker | null {
  if (typeof Worker === 'undefined') return null;
  try {
    return new Worker(
      new URL('./coverage-geometry.worker.ts', import.meta.url),
      {
        type: 'module',
      }
    ) as unknown as CoverageWorker;
  } catch {
    return null;
  }
}

/**
 * The map's coverage (computeCoverage) for these footprints, worked out in a Web Worker so a
 * State-scale union, intersection and cut don't freeze the page. Each change posts a request with
 * a new id, and a result for an older id is dropped: only the latest picks' coverage is drawn.
 * While a result for the current inputs is being worked out it is null, the first time and after
 * every change: an answer for the previous picks is never returned as current (it could draw green
 * for a domain just cleared). With no Worker (`create` null, as in jsdom, a worker that can't be made, or one
 * that errors: `onerror` or `onmessageerror`), the
 * same function runs on the main thread, synchronously, as before.
 *
 * Re-posts only when `footprints`, `blocked` or `loading` change: the caller keys `footprints` on
 * the coverage picks (the shape-carrying five and Type Curve), so a Price Deck or DCA pick posts
 * nothing.
 *
 * @param {ScenarioFootprint[]} footprints - The picked coverage scenarios' footprints.
 * @param {boolean} blocked - A domain is missing or archived.
 * @param {boolean} loading - A changed pick's footprint has not arrived.
 * @param {Function | null} [create] - Makes the worker; null for none. Tests pass a stand-in.
 * @return {CoverageResult | null} The coverage, or null while the current inputs' is worked out.
 */
export function useCoverageGeometry(
  footprints: readonly ScenarioFootprint[],
  blocked: boolean,
  loading: boolean,
  create: (() => CoverageWorker | null) | null = typeof Worker === 'undefined'
    ? null
    : createCoverageWorker
): CoverageResult | null {
  const [createWorker] = useState(() => create);
  const [workerFailed, setWorkerFailed] = useState(false);
  const worker = useRef<CoverageWorker | null>(null);
  const latest = useRef(0);
  // The inputs the newest request was posted with, so an answer is shown only for its own.
  const latestInputs = useRef<Omit<CoverageRequest, 'id'> | null>(null);
  const [answer, setAnswer] = useState<
    (CoverageResponse & { inputs: Omit<CoverageRequest, 'id'> }) | null
  >(null);
  const viaWorker = createWorker !== null && !workerFailed;

  // Made in the effect, not during render, so a remount (StrictMode's included) gets a live one.
  useEffect(() => {
    if (!createWorker) return undefined;
    const made = createWorker();
    if (!made) {
      setWorkerFailed(true);
      return undefined;
    }
    made.onmessage = (event) => {
      const inputs = latestInputs.current;
      if (event.data.id !== latest.current || !inputs) return;
      setAnswer({ ...event.data, inputs });
    };
    // A worker that fails to load (a blocked or missing chunk), throws, or sends what can't be
    // read would leave the map 'pending' for good: fall back to the main thread instead.
    const fail = () => {
      made.terminate();
      worker.current = null;
      setWorkerFailed(true);
    };
    made.onerror = fail;
    made.onmessageerror = fail;
    worker.current = made;
    return () => {
      made.onerror = null;
      made.onmessageerror = null;
      made.terminate();
      worker.current = null;
    };
  }, [createWorker]);

  useEffect(() => {
    if (!worker.current) return;
    latest.current += 1;
    latestInputs.current = { footprints, blocked, loading };
    worker.current.postMessage({
      id: latest.current,
      footprints,
      blocked,
      loading,
    });
  }, [footprints, blocked, loading, workerFailed]);

  const onMainThread = useMemo(() => {
    if (viaWorker) return null;
    return computeCoverage(footprints, { blocked, loading });
  }, [viaWorker, footprints, blocked, loading]);
  const current =
    answer !== null &&
    answer.inputs.footprints === footprints &&
    answer.inputs.blocked === blocked &&
    answer.inputs.loading === loading;
  if (!viaWorker) return onMainThread;
  return current ? answer.result : null;
}
